import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, type MessageDto } from "@keydials/api-client";
import { RichText, richTextHasContent, HELP_ANCHORS } from "@keydials/ui";
import { RichTextEditor } from "../components/RichTextEditor";
import { useWorkspaceServices } from "../context";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "../vocabulary";
import { TranscriptExportButtons } from "./TranscriptExportButtons";
import { threadOrder, type ExportMessage } from "./transcriptExport";

/**
 * One person the current user may address on a restricted message (KEY-285). The host supplies the dial's
 * roster already narrowed by the target mask; this component narrows it again to the people whose grants
 * cover the node being discussed, because a recipient who cannot read the node would never see it.
 */
export interface RestrictedMessaging {
  /** Whether the current user may START a restricted exchange. Replying to one they were addressed on
   * needs no such permission — no audience is being chosen there. */
  canPost: boolean;
  /** Who they may address, already narrowed by the dial's target mask. Empty hides the control. */
  recipients: RestrictedRecipientOption[];
  /**
   * Everyone who could be @mentioned on this dial (KEY-592) — its members, whatever the restricted-messaging
   * rules say, since a mention notifies rather than addresses. Narrowed per thread to people who can see the
   * action point (and, on a restricted message, to its audience). Omitted: no mentions offered.
   */
  mentionable?: RestrictedRecipientOption[];
}

export interface RestrictedRecipientOption {
  userId: string;
  name: string;
  role: string;
  /** Every membership grant this person holds on the dial: a null sectionKey covers the whole dial, a
   * null segmentKey the whole section. */
  /** `itemKey` (KEY-852) narrows a grant to one item of the action point. */
  scopes: { sectionKey: string | null; segmentKey: string | null; itemKey?: string | null }[];
}

type Props = {
  instanceId: string;
  sectionKey: string;
  segmentKey: string;
  /** Set for one item's thread (itemised dials); omit for the segment-level thread. */
  itemKey?: string;
  meUserId: string | null;
  /** Whether the current user may post (an instance member or manager). */
  canPost: boolean;
  /** Whether the current user may delete anyone's message — a dial administrator: an instance-wide Admin
   *  grant, or an org instance-manager (KEY-582). Nobody, this person included, may edit a message once it
   *  is sent; a deleted one stays in the thread saying who removed it and when. */
  canModerate: boolean;
  /** Restricted messaging (KEY-285); omit where the host can't offer it (e.g. a draft preview). */
  restricted?: RestrictedMessaging;
  /** Tighter styling + wording for an inline item thread. */
  compact?: boolean;
  /** This dial's word for an action point (KEY-539). This thread is mounted with keys rather than the
   *  instance detail, so the vocabulary arrives as a prop; omitted means the platform's word. */
  noun?: SegmentNoun;
  /** KEY-591: what this thread is, for its download / print — "Dial › Section › Action point" (› item). Without it
   *  the thread offers no export. */
  exportTitle?: string;
  /** Lines under the export's title: the organisation, and so on. */
  exportSubtitle?: (string | null | undefined)[];
};

let tempCounter = 0;
const nextTempId = () => `temp-${++tempCounter}`;
const isTemp = (id: string) => id.startsWith("temp-");

function authorName(m: MessageDto): string {
  return m.authorDisplayName?.trim() || m.authorEmail || "Unknown";
}

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

const isRestricted = (m: MessageDto) => m.visibility === "Restricted";

/**
 * What a deleted message says in its place (KEY-582). The author and time stay above it: a record an
 * administrator could quietly thin is not a record, so the gap says who closed it and when. The fallback
 * covers the optimistic flip before the server's answer arrives, and a deleter whose account is gone.
 */
export function deletedLine(m: MessageDto): string {
  const who = m.deletedByDisplayName?.trim() || m.deletedByEmail || "a dial administrator";
  const at = m.deletedAtUtc ? when(m.deletedAtUtc) : "";
  return at ? `Deleted by ${who} · ${at}` : `Deleted by ${who}`;
}

/**
 * Every restricted message states its own audience, in the message — not in a tooltip and not on a help
 * page. Recipients need it to judge how frankly they may reply; senders need it to confirm what they just
 * did. It names the administrators on purpose: a message two classes of admin can read is not private,
 * which is why the word throughout this feature is "restricted".
 */
/** KEY-591: a message reduced to what a downloaded / printed transcript shows — the same words the screen uses. */
export function toExportMessage(m: MessageDto, meUserId: string | null, context?: string | null, depth = 0): ExportMessage {
  return {
    author: authorName(m),
    at: `${when(m.createdAtUtc)}${m.editedAtUtc ? " (edited)" : ""}`,
    body: m.body,
    context,
    deleted: m.isDeleted ? deletedLine(m) : null,
    restricted: isRestricted(m) ? audienceLine(m, meUserId) : null,
    depth,
  };
}

export function audienceLine(m: MessageDto, meUserId: string | null): string {
  const recipients = m.recipients ?? [];
  const others = recipients.filter((r) => r.userId !== meUserId).map((r) => r.displayName?.trim() || r.email);
  const names = recipients.some((r) => r.userId === meUserId) ? ["you", ...others] : others;
  const who = names.length > 0 ? `${names.join(", ")}, and dial administrators` : "dial administrators";
  return `Restricted — ${who}. Key Dials support can access messages when investigating a fault.`;
}

/** A grant covers this node when it is dial-wide, section-wide over this section, exactly this segment — or, for an
 * item grant (KEY-852), exactly this item: never the action point's own thread, never a sibling's. */
function covers(option: RestrictedRecipientOption, sectionKey: string, segmentKey: string, itemKey?: string): boolean {
  return option.scopes.some(
    (s) => s.sectionKey === null || (s.sectionKey === sectionKey
      && (s.segmentKey === null || (s.segmentKey === segmentKey && (!s.itemKey || s.itemKey === itemKey)))),
  );
}

/**
 * One message in a READ-ONLY transcript: the item transcript under an action point's own thread, and the
 * section and whole-dial Discussion tabs (KEY-559). No reply or delete — those happen in the thread the
 * message belongs to. A deleted message is a tombstone here as it is there (KEY-582).
 *
 * Shared on purpose. A restricted message has to state its audience wherever it is shown (KEY-285), and the
 * item transcript used to hand-roll its own row that didn't, so a restricted item comment read there like
 * any other. One row means a transcript can't be added that forgets.
 */
export function TranscriptMessageRow({
  message: m,
  meUserId,
  context,
}: {
  message: MessageDto;
  meUserId: string | null;
  /** Where in the group this was said, when that isn't obvious from the heading above it — an item's name. */
  context?: string | null;
}) {
  const restricted = isRestricted(m);
  return (
    <div
      style={
        restricted
          ? { background: RESTRICTED_TINT, border: `var(--border-w) solid ${RESTRICTED_EDGE}`, borderRadius: "var(--radius-sm)", padding: "8px 10px" }
          : undefined
      }
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 2, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text)" }}>{authorName(m)}</span>
        <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>{when(m.createdAtUtc)}</span>
        {m.editedAtUtc && <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>(edited)</span>}
        {context && <span style={contextChip}>{context}</span>}
        {restricted && <span style={restrictedChip}>Restricted</span>}
      </div>
      {m.isDeleted ? (
        <div style={{ fontSize: 13.5, color: "var(--text-faint)", fontStyle: "italic" }}>{deletedLine(m)}</div>
      ) : (
        <RichText value={m.body} style={{ fontSize: 13.5, color: "var(--text-muted)" }} />
      )}
      {restricted && !m.isDeleted && (
        <div style={{ marginTop: 4, fontSize: 12, color: "var(--text-muted)" }}>{audienceLine(m, meUserId)}</div>
      )}
    </div>
  );
}

const contextChip = {
  fontSize: 11,
  fontWeight: 600,
  padding: "1px 7px",
  borderRadius: 99,
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface-2)",
  color: "var(--text-muted)",
} as const;

export function ConversationThread({
  instanceId,
  sectionKey,
  segmentKey,
  itemKey,
  meUserId,
  canPost,
  canModerate,
  restricted,
  compact = false,
  noun = DEFAULT_SEGMENT_NOUN,
  exportTitle,
  exportSubtitle,
}: Props) {
  const { api, getAccessToken, useSegmentRealtime } = useWorkspaceServices();
  const [messages, setMessages] = useState<MessageDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState<MessageDto | null>(null);
  const [busy, setBusy] = useState(false);
  // Choosing recipients IS choosing restricted mode — there is no separate toggle to disagree with it,
  // so "the dropdown said restricted but I'd cleared the names" cannot happen.
  const [recipientIds, setRecipientIds] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const reload = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    setLoading(true);
    try {
      setMessages(await api.listMessages(token, instanceId, sectionKey, segmentKey, itemKey));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load the discussion.");
    } finally {
      setLoading(false);
    }
  }, [instanceId, sectionKey, segmentKey, itemKey]);

  // Reset thread state whenever the selected segment changes, then load it.
  useEffect(() => {
    setReplyTo(null);
    setDraft("");
    setRecipientIds([]);
    setPickerOpen(false);
    void reload();
  }, [reload]);

  // Live updates for this thread: reload on a pushed event and after a reconnect (backfill). A restricted
  // message arrives as a per-user ping rather than a scope one, which this same handler picks up.
  useSegmentRealtime(instanceId, sectionKey, segmentKey, { onEvent: () => void reload(), onReconnect: reload });

  const childrenByParent = useMemo(() => {
    const map = new Map<string | null, MessageDto[]>();
    for (const m of messages) {
      const arr = map.get(m.parentMessageId) ?? [];
      arr.push(m);
      map.set(m.parentMessageId, arr);
    }
    return map;
  }, [messages]);

  // Only people whose grants reach this action point: a message addressed to anyone else would be
  // invisible to them forever, with no error and nothing they could act on.
  const candidates = useMemo(
    () => (restricted?.recipients ?? []).filter((o) => o.userId !== meUserId && covers(o, sectionKey, segmentKey, itemKey)),
    [restricted, meUserId, sectionKey, segmentKey, itemKey],
  );

  // A reply to a restricted message is restricted, full stop — the server enforces it, and the composer
  // shows it as a locked state rather than a pre-selected default anyone could flip.
  const lockedToParent = replyTo && isRestricted(replyTo) ? replyTo : null;
  const canOfferRestrict = restricted?.canPost === true && candidates.length > 0 && !lockedToParent;
  const chosen = candidates.filter((c) => recipientIds.includes(c.userId));
  const restrictedNow = !!lockedToParent || chosen.length > 0;

  // KEY-592: who "@" offers here. Only people who can see this action point — the server refuses anyone else —
  // and on a restricted message only its audience, since telling anyone else would be the leak restriction exists
  // to prevent.
  const mentionables = useMemo(() => {
    const audience = lockedToParent
      ? new Set((lockedToParent.recipients ?? []).map((r) => r.userId))
      : chosen.length > 0
        ? new Set(chosen.map((c) => c.userId))
        : null;
    return (restricted?.mentionable ?? [])
      .filter((o) => o.userId !== meUserId && covers(o, sectionKey, segmentKey, itemKey) && (!audience || audience.has(o.userId)))
      .map((o) => ({ userId: o.userId, name: o.name, hint: o.role }));
  }, [restricted, meUserId, sectionKey, segmentKey, itemKey, lockedToParent, chosen]);

  function sendLabel(): string {
    if (lockedToParent) {
      return "Reply restricted";
    }
    if (chosen.length === 0) {
      return replyTo ? "Reply publicly" : "Post publicly";
    }
    const shown = chosen.slice(0, 2).map((c) => c.name);
    const rest = chosen.length - shown.length;
    const who = rest > 0 ? `${shown.join(", ")} +${rest}` : shown.join(", ");
    return `${replyTo ? "Reply" : "Post"} restricted to ${who}`;
  }

  async function submit() {
    const body = draft;
    const token = getAccessToken();
    if (!richTextHasContent(body) || !token || busy) {
      return;
    }
    setBusy(true);
    const tempId = nextTempId();
    const parentId = replyTo?.id ?? null;
    const sending = chosen.map((c) => c.userId);
    const optimistic: MessageDto = {
      id: tempId,
      parentMessageId: parentId,
      body,
      authorUserId: meUserId ?? "",
      authorEmail: "You",
      authorDisplayName: "You",
      createdAtUtc: new Date().toISOString(),
      editedAtUtc: null,
      isDeleted: false,
      visibility: restrictedNow ? "Restricted" : "Public",
      recipients: restrictedNow ? (lockedToParent?.recipients ?? chosen.map((c) => ({ userId: c.userId, email: c.name, displayName: c.name }))) : null,
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    setReplyTo(null);
    // Mode never sticks: the next message starts public again, visibly. Sticky mode is how the
    // second-message leak happens in every product that ships this feature.
    setRecipientIds([]);
    setPickerOpen(false);
    try {
      const created = await api.postMessage(token, instanceId, {
        sectionKey,
        segmentKey,
        itemKey,
        body,
        parentMessageId: parentId,
        ...(sending.length > 0 ? { recipientUserIds: sending } : {}),
      });
      setMessages((prev) => prev.map((m) => (m.id === tempId ? created : m)));
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setDraft(body); // restore the text so it isn't lost
      setRecipientIds(sending); // and the audience, so the retry doesn't silently go public
      setError(
        e instanceof ApiError && e.status === 403
          ? "You don't have permission to post here."
          : e instanceof ApiError && e.status === 400 && /mention/i.test(e.message)
            ? `Everyone you @mention has to be able to see this ${noun.one}${restrictedNow ? ", and be one of the people this message goes to" : ""}.`
            : e instanceof ApiError && e.status === 400
              ? `Everyone a restricted message names has to be able to see this ${noun.one}.`
              : "Could not post the message.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    const previous = messages.find((m) => m.id === id);
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, isDeleted: true, body: "" } : m)));
    try {
      await api.deleteMessage(token, instanceId, id);
      // The server's tombstone names the deleter and the time; the optimistic one above cannot.
      void reload();
    } catch (e) {
      if (previous) {
        setMessages((prev) => prev.map((m) => (m.id === id ? previous : m)));
      }
      setError(e instanceof Error ? e.message : "Could not delete the message.");
    }
  }

  function renderNode(m: MessageDto, depth: number): React.ReactNode {
    // Deleting is a dial administrator's act, never the author's (KEY-582); there is no editing at all.
    const canDelete = canModerate && !m.isDeleted && !isTemp(m.id);
    const children = childrenByParent.get(m.id) ?? [];
    const indent = Math.min(depth, 4) * 18;
    const restricted = isRestricted(m);

    return (
      <div key={m.id} style={{ marginLeft: indent }}>
        <div
          style={{
            background: restricted ? RESTRICTED_TINT : "var(--surface)",
            border: `var(--border-w) solid ${restricted ? RESTRICTED_EDGE : "var(--border)"}`,
            borderRadius: "var(--radius-sm)",
            padding: "10px 12px",
            marginBottom: 8,
            opacity: isTemp(m.id) ? 0.6 : 1,
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, fontSize: 13.5, color: "var(--text)" }}>{authorName(m)}</span>
            <span style={{ fontSize: 12, color: "var(--text-faint)" }}>{when(m.createdAtUtc)}</span>
            {m.editedAtUtc && <span style={{ fontSize: 12, color: "var(--text-faint)" }}>(edited)</span>}
            {restricted && <span style={restrictedChip}>Restricted</span>}
          </div>

          {m.isDeleted ? (
            <div style={{ fontSize: 14, color: "var(--text-faint)", fontStyle: "italic" }}>{deletedLine(m)}</div>
          ) : (
            <RichText value={m.body} style={{ fontSize: 14, color: "var(--text)" }} />
          )}

          {restricted && !m.isDeleted && (
            <div style={{ marginTop: 6, fontSize: 12, color: "var(--text-muted)" }}>{audienceLine(m, meUserId)}</div>
          )}

          <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
            {canPost && !m.isDeleted && !isTemp(m.id) && (
              <button onClick={() => setReplyTo(m)} style={linkBtn}>Reply</button>
            )}
            {canDelete && (
              <button onClick={() => remove(m.id)} style={{ ...linkBtn, color: "var(--st-overdue)" }}>Delete</button>
            )}
          </div>
        </div>

        {children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  }

  const roots = childrenByParent.get(null) ?? [];

  return (
    <section>
      {error && (
        <p style={{ marginBottom: 12, color: "var(--st-overdue)", fontSize: 13 }}>{error}</p>
      )}

      {loading && messages.length === 0 ? (
        <p style={{ color: "var(--text-faint)", fontSize: 13.5 }}>Loading…</p>
      ) : roots.length === 0 ? (
        <p style={{ color: "var(--text-faint)", fontSize: compact ? 12.5 : 13.5, marginBottom: compact ? 8 : 14 }}>
          {compact ? "No comments on this item yet." : "No messages yet."}
        </p>
      ) : (
        <>
          {exportTitle && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
              <TranscriptExportButtons
                build={() => ({
                  title: exportTitle,
                  subtitle: exportSubtitle,
                  groups: [
                    {
                      messages: threadOrder(messages.filter((m) => !isTemp(m.id))).map(({ message, depth }) =>
                        toExportMessage(message, meUserId, null, depth),
                      ),
                    },
                  ],
                })}
              />
            </div>
          )}
          <div style={{ marginBottom: 14 }}>{roots.map((m) => renderNode(m, 0))}</div>
        </>
      )}

      {canPost ? (
        <div
          style={{
            // The composer itself says which mode it is in — mode must be legible without reading a
            // control somewhere else on the screen.
            background: restrictedNow ? RESTRICTED_TINT : undefined,
            border: restrictedNow ? `var(--border-w) solid ${RESTRICTED_EDGE}` : undefined,
            borderRadius: restrictedNow ? "var(--radius-sm)" : undefined,
            padding: restrictedNow ? 10 : undefined,
          }}
        >
          {replyTo && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 12.5, color: "var(--text-faint)" }}>
              <span>Replying to {authorName(replyTo)}</span>
              <button onClick={() => setReplyTo(null)} style={linkBtn}>Cancel</button>
            </div>
          )}

          {lockedToParent && (
            <div style={{ marginBottom: 8, fontSize: 12.5, color: "var(--text-muted)" }}>
              <span style={restrictedChip}>Restricted</span>{" "}
              This reply goes to the same people as the message you're answering — it can't be made public.{" "}
              {audienceLine(lockedToParent, meUserId)}
            </div>
          )}

          {!lockedToParent && chosen.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
              <span style={restrictedChip}>Restricted to</span>
              {chosen.map((c) => (
                <button
                  key={c.userId}
                  type="button"
                  onClick={() => setRecipientIds((ids) => ids.filter((id) => id !== c.userId))}
                  title={`Remove ${c.name}`}
                  style={recipientChip}
                >
                  {c.name} ×
                </button>
              ))}
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>and dial administrators</span>
            </div>
          )}

          <div data-help-anchor={HELP_ANCHORS.messageEditor}>
            <RichTextEditor
              value={draft}
              onChange={setDraft}
              placeholder={compact ? "Add a comment on this item…" : "Write a message…"}
              minHeight={compact ? 56 : 72}
              mentionables={mentionables}
            />
          </div>

          {canOfferRestrict && pickerOpen && (
            <div
              style={{
                marginTop: 8,
                padding: 10,
                border: "var(--border-w) solid var(--border)",
                borderRadius: "var(--radius-sm)",
                background: "var(--surface)",
              }}
            >
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
                Only people who can see this {noun.one} are listed. Dial administrators and Key Dials
                support can read restricted messages too.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 190, overflowY: "auto" }}>
                {candidates.map((c) => (
                  <label key={c.userId} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={recipientIds.includes(c.userId)}
                      onChange={(e) =>
                        setRecipientIds((ids) => (e.target.checked ? [...ids, c.userId] : ids.filter((id) => id !== c.userId)))
                      }
                    />
                    <span style={{ color: "var(--text)" }}>{c.name}</span>
                    <span style={{ color: "var(--text-faint)", fontSize: 12 }}>{c.role}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={() => void submit()}
              disabled={!richTextHasContent(draft) || busy}
              data-help-anchor={HELP_ANCHORS.messageSend}
              style={{
                ...primaryBtn,
                // The label carries the mode: it is the last thing read before the click.
                background: restrictedNow ? RESTRICTED_BUTTON : "var(--accent)",
                opacity: !richTextHasContent(draft) || busy ? 0.5 : 1,
              }}
            >
              {sendLabel()}
            </button>
            {canOfferRestrict && (
              <button type="button" onClick={() => setPickerOpen((o) => !o)} style={ghostBtn}>
                {chosen.length > 0 ? "Change who can see it" : "Restrict to…"}
              </button>
            )}
            {!lockedToParent && chosen.length > 0 && (
              <button type="button" onClick={() => setRecipientIds([])} style={linkBtn}>
                Make it public
              </button>
            )}
            {mentionables.length > 0 && (
              // KEY-592: following a dial no longer sends its messages, so this is how a message reaches someone.
              <span style={{ fontSize: 12, color: "var(--text-faint)" }}>Type @ to notify someone</span>
            )}
          </div>
        </div>
      ) : compact ? null : (
        <p style={{ color: "var(--text-faint)", fontSize: 13 }}>Join this dial to take part in the discussion.</p>
      )}
    </section>
  );
}

// A restricted message must be unmistakable at a glance, in either theme, without depending on a token
// that means something else elsewhere in the product.
const RESTRICTED_TINT = "rgba(180, 120, 0, 0.10)";
const RESTRICTED_EDGE = "rgba(180, 120, 0, 0.45)";
const RESTRICTED_BUTTON = "#8a5a00";

const restrictedChip = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: "uppercase",
  padding: "2px 7px",
  borderRadius: 99,
  border: `1px solid ${RESTRICTED_EDGE}`,
  background: RESTRICTED_TINT,
  color: "var(--text)",
} as const;

const recipientChip = {
  font: "inherit",
  fontSize: 12,
  fontWeight: 600,
  padding: "3px 9px",
  borderRadius: 99,
  border: `1px solid ${RESTRICTED_EDGE}`,
  background: "var(--surface)",
  color: "var(--text)",
  cursor: "pointer",
} as const;

const primaryBtn = {
  font: "inherit",
  fontSize: 13.5,
  fontWeight: 600,
  padding: "8px 16px",
  borderRadius: "var(--radius-sm)",
  border: "none",
  background: "var(--accent)",
  color: "var(--on-accent, #fff)",
  cursor: "pointer",
} as const;

const ghostBtn = {
  font: "inherit",
  fontSize: 13.5,
  fontWeight: 600,
  padding: "8px 16px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "none",
  color: "var(--text)",
  cursor: "pointer",
} as const;

const linkBtn = {
  font: "inherit",
  fontSize: 12.5,
  fontWeight: 600,
  padding: 0,
  border: "none",
  background: "none",
  color: "var(--text-faint)",
  cursor: "pointer",
} as const;
